import {
    type FrontendKey,
    type Persona,
    personaToFrontend,
    grantsAdministration,
    isDeskSectionVisible,
} from '@septima/contract';

// The four staff views, in canonical order. Login lands on the first granted
// view; the sidebar switcher renders the user's granted subset.
export const FRONTEND_KEYS: readonly FrontendKey[] = ['desk', 'ops', 'comply', 'view'];

export interface FrontendMeta {
    key: FrontendKey;
    /** Full brand label, e.g. shown in the sidebar header. */
    label: string;
    /** Short label, e.g. shown on the switcher buttons. */
    shortLabel: string;
}

export const FRONTENDS: Record<FrontendKey, FrontendMeta> = {
    desk: { key: 'desk', label: 'Septima Desk', shortLabel: 'Desk' },
    ops: { key: 'ops', label: 'Septima Ops', shortLabel: 'Ops' },
    comply: { key: 'comply', label: 'Septima Comply', shortLabel: 'Comply' },
    view: { key: 'view', label: 'Septima View', shortLabel: 'View' },
};

// Sidebar sections for each frontend
export interface SidebarSection {
    key: string;
    label: string;
    href: string;
    icon: string;
    badge?: number | string;
    /**
     * Legacy / equivalent routes that resolve to this same section — used for
     * active-state highlighting and route authorisation when a row's canonical
     * `href` was renamed (e.g. the desk landing moved `/desk/my-book` →
     * `/desk/dashboard` but the old URL still renders the same page).
     */
    aliases?: string[];
    /**
     * Globally retired: removed from the sidebar AND from route authorisation —
     * a 403 for EVERY persona (filtered in `visibleSectionsFor`, which both the
     * sidebar and `isRouteAuthorised` derive from). Distinct from the
     * per-persona `isDeskSectionVisible` policy, which only hides a row for a
     * single persona. The section + its route stay declared (reversible) — the
     * page is reachable again the moment the flag is dropped.
     */
    hidden?: boolean;
}

export const deskSections: SidebarSection[] = [
    {
        key: 'my-book',
        label: 'Desk Dashboard',
        href: '/desk/dashboard',
        icon: 'LayoutDashboard',
        aliases: ['/desk/my-book'],
    },
    { key: 'onboarding', label: 'My Onboarding', href: '/desk/onboarding', icon: 'Rocket' },
    { key: 'client-book', label: 'Client Book', href: '/desk/client-book', icon: 'Users' },
    {
        key: 'overdrawn-cash',
        label: 'Overdrawn Cash',
        href: '/desk/cash/overdrawn',
        icon: 'AlertTriangle',
    },
    { key: 'portfolio', label: 'Portfolio', href: '/desk/portfolio', icon: 'PieChart' },
    { key: 'actions', label: 'Actions', href: '/desk/actions', icon: 'CheckSquare', hidden: true },
    {
        key: 'corporate-actions',
        label: 'Corporate Actions',
        href: '/desk/corporate-actions',
        icon: 'Building2',
    },
    {
        key: 'calls-meetings',
        label: 'Calls & Meetings',
        href: '/desk/calls-meetings',
        icon: 'Phone',
    },
    {
        key: 'suitability',
        label: 'Suitability',
        href: '/desk/suitability',
        icon: 'Shield',
    },
    {
        key: 'desk-vulnerability',
        label: 'Vulnerable Clients',
        href: '/desk/vulnerability',
        icon: 'Heart',
    },
    {
        // The fact-find register + "Create fact find" (Individual / Entity)
        // entry point and the per-client prefill / risk / signing workbench
        // (fact-find pipeline). This is the canonical "Fact Find" surface: it
        // holds the fact finds with their PDF / signing arrangements, so it
        // takes the "Fact Find" label and slot. The older client-reviews
        // register (below, hidden) embeds the same fact-find document per
        // client but has no create-from-scratch affordance.
        key: 'fact-find',
        label: 'Fact Find',
        href: '/desk/fact-find',
        icon: 'FileText',
    },
    {
        // Superseded by the fact-find register above. Route kept reachable for
        // existing deep links but hidden from the sidebar.
        key: 'client-reviews',
        label: 'Fact Find',
        href: '/desk/client-reviews',
        icon: 'ClipboardCheck',
        hidden: true,
    },
    {
        key: 'client-knowledge',
        label: 'Client Knowledge',
        href: '/desk/client-knowledge',
        icon: 'Users',
    },
    { key: 'my-page', label: 'My Page', href: '/desk/my-page', icon: 'LayoutDashboard' },
    { key: 'agents', label: 'Agents', href: '/desk/agents', icon: 'Sparkles' },
    { key: 'workflows', label: 'Workflows', href: '/desk/workflows', icon: 'Workflow' },
    { key: 'settings', label: 'Settings', href: '/desk/settings', icon: 'Settings' },
];

export const opsSections: SidebarSection[] = [
    {
        key: 'command-centre',
        label: 'Ops Dashboard',
        href: '/ops/dashboard',
        icon: 'LayoutDashboard',
        aliases: ['/ops/command-centre'],
    },
    { key: 'reconciliation', label: 'Reconciliation', href: '/ops/reconciliation', icon: 'Scale' },
    {
        key: 'corporate-actions',
        label: 'Corporate Actions',
        href: '/ops/corporate-actions',
        icon: 'Building2',
    },
    { key: 'onboarding', label: 'Onboarding Pipeline', href: '/ops/onboarding', icon: 'Rocket' },
    {
        key: 'trade-operations',
        label: 'Trade Operations',
        href: '/ops/trade-operations',
        icon: 'ArrowRightLeft',
    },
    {
        key: 'cash-management',
        label: 'Cash Management',
        href: '/ops/cash-management',
        icon: 'Wallet',
    },
    {
        key: 'overdrawn-cash',
        label: 'Overdrawn Cash',
        href: '/ops/cash/overdrawn',
        icon: 'AlertTriangle',
    },
    { key: 'client-book', label: 'Client Book', href: '/ops/client-book', icon: 'Users' },
    {
        key: 'document-vault',
        label: 'Document Vault',
        href: '/ops/document-vault',
        icon: 'FileText',
    },
    { key: 'reporting', label: 'Reporting', href: '/ops/reporting', icon: 'FileBarChart' },
    { key: 'fees-billing', label: 'Fees & Billing', href: '/ops/fees-billing', icon: 'Receipt' },
    { key: 'agents', label: 'Agents', href: '/ops/agents', icon: 'Sparkles' },
    { key: 'workflows', label: 'Workflows', href: '/ops/workflows', icon: 'Workflow' },
    { key: 'settings', label: 'Settings', href: '/ops/settings', icon: 'Settings' },
];

export const complySections: SidebarSection[] = [
    {
        key: 'dashboard',
        label: 'Comply Dashboard',
        href: '/comply/dashboard',
        icon: 'LayoutDashboard',
    },
    {
        key: 'consumer-duty',
        label: 'Consumer Duty',
        href: '/comply/consumer-duty',
        icon: 'Heart',
    },
    {
        key: 'cmp-reviews',
        label: 'CMP Reviews',
        href: '/comply/cmp-reviews',
        icon: 'ClipboardCheck',
    },
    {
        key: 'client-book',
        label: 'Client Book',
        href: '/comply/client-book',
        icon: 'Users',
    },
    {
        key: 'smcr-directory',
        label: 'SMCR Directory',
        href: '/comply/smcr-directory',
        icon: 'UserCheck',
    },
    { key: 'suitability', label: 'Suitability', href: '/comply/suitability', icon: 'Shield' },
    {
        key: 'fin-crime-kyc',
        label: 'Financial Crime & KYC',
        href: '/comply/fin-crime-kyc',
        icon: 'AlertTriangle',
    },
    {
        key: 'overdrawn-escalations',
        label: 'Overdrawn Escalations',
        href: '/comply/overdrawn-escalations',
        icon: 'Wallet',
    },
    { key: 'onboarding', label: 'Onboarding Pipeline', href: '/comply/onboarding', icon: 'Rocket' },
    {
        key: 'vulnerability',
        label: 'Vulnerability',
        href: '/comply/vulnerability',
        icon: 'Heart',
    },
    {
        key: 'fair-value',
        label: 'Fair Value',
        href: '/comply/fair-value',
        icon: 'Scale',
    },
    {
        key: 'data-freshness',
        label: 'Data Freshness',
        href: '/comply/data-freshness',
        icon: 'Activity',
    },
    {
        key: 'tc-tracker',
        label: 'T&C Tracker',
        href: '/comply/tc-tracker',
        icon: 'GraduationCap',
    },
    {
        key: 'breach-register',
        label: 'Breach Register',
        href: '/comply/breach-register',
        icon: 'FileWarning',
    },
    {
        key: 'atlas-intelligence',
        label: 'ATLAS Intelligence',
        href: '/comply/call-intelligence',
        icon: 'BrainCircuit',
    },
    {
        key: 'reg-capital',
        label: 'Regulatory Capital',
        href: '/comply/reg-capital',
        icon: 'Landmark',
    },
    { key: 'my-page', label: 'My Page', href: '/comply/my-page', icon: 'LayoutDashboard' },
    { key: 'agents', label: 'Agents', href: '/comply/agents', icon: 'Sparkles' },
    { key: 'workflows', label: 'Workflows', href: '/comply/workflows', icon: 'Workflow' },
    { key: 'settings', label: 'Settings', href: '/comply/settings', icon: 'Settings' },
];

export const viewSections: SidebarSection[] = [
    {
        key: 'firm-overview',
        label: 'View Dashboard',
        href: '/view/dashboard',
        icon: 'LayoutDashboard',
        aliases: ['/view/firm-overview'],
    },
    {
        key: 'financial-performance',
        label: 'Financial Performance',
        href: '/view/financial-performance',
        icon: 'TrendingUp',
    },
    {
        key: 'investment-performance',
        label: 'Investment Performance',
        href: '/view/investment-performance',
        icon: 'LineChart',
    },
    { key: 'kpi', label: 'KPI', href: '/view/kpi', icon: 'BarChart3' },
    {
        key: 'operational-intelligence',
        label: 'Operational Intelligence',
        href: '/view/operational-intelligence',
        icon: 'Activity',
    },
    { key: 'client-book', label: 'Client Book', href: '/view/client-book', icon: 'Users' },
    {
        key: 'client-intelligence',
        label: 'Client Intelligence',
        href: '/view/client-intelligence',
        icon: 'Users',
    },
    {
        key: 'custodian-analytics',
        label: 'Custodian Analytics',
        href: '/view/custodian-analytics',
        icon: 'Building2',
    },
    {
        key: 'business-development',
        label: 'Business Development',
        href: '/view/business-development',
        icon: 'Rocket',
    },
    { key: 'onboarding', label: 'Onboarding Pipeline', href: '/view/onboarding', icon: 'Rocket' },
    {
        key: 'risk-register',
        label: 'Risk Register',
        href: '/view/risk-register',
        icon: 'AlertOctagon',
    },
    { key: 'benchmarking', label: 'Benchmarking', href: '/view/benchmarking', icon: 'BarChart3' },
    {
        key: 'agent-activity-log',
        label: 'Agent Activity Log',
        href: '/view/agent-activity-log',
        icon: 'Activity',
    },
    {
        key: 'data-architecture',
        label: 'Data Architecture',
        href: '/view/data-architecture',
        icon: 'Plug',
    },
    { key: 'agents', label: 'Agents', href: '/view/agents', icon: 'Sparkles' },
    { key: 'workflows', label: 'Workflows', href: '/view/workflows', icon: 'Workflow' },
    // Administration (ADR-0013) — the firm-configuration home (User Management +
    // Operational Tolerances). Revealed only to a granted-`admin` user (the
    // SuperAdmin), filtered in `visibleSectionsFor`; placed just before
    // Settings.
    {
        key: 'administration',
        label: 'Administration',
        href: '/view/administration',
        icon: 'ShieldCheck',
    },
    { key: 'settings', label: 'Settings', href: '/view/settings', icon: 'Settings' },
];

/** The sidebar key for the admin-only View → Administration section. */
export const ADMINISTRATION_SECTION_KEY = 'administration';

function sectionsForFrontend(frontend: FrontendKey): SidebarSection[] {
    switch (frontend) {
        case 'desk':
            return deskSections;
        case 'ops':
            return opsSections;
        case 'comply':
            return complySections;
        case 'view':
            return viewSections;
    }
}

/**
 * The sidebar sections visible for the active persona, after the policy filter
 * (ADR-0013). Resolves the persona to its frontend (`im-assistant → desk`),
 * then:
 *   - on Desk, omits the suitability + vulnerability rows for an `im-assistant`
 *     (`isDeskSectionVisible`);
 *   - on View, reveals the Administration row only when the granted tokens
 *     include `admin` (`showAdministration`).
 *
 * Replaces the bare `getSectionsForFrontend(frontend)` call the shell used to
 * make — the single place section visibility is decided.
 */
export function visibleSectionsFor(
    activePersona: Persona,
    grantedTokens: readonly string[],
): SidebarSection[] {
    const frontend = personaToFrontend(activePersona);
    const showAdmin = grantsAdministration(grantedTokens);
    return sectionsForFrontend(frontend).filter((section) => {
        // Globally retired rows (`hidden`) are gone for every persona — drop
        // them from the sidebar AND from route authorisation (this function
        // backs `isRouteAuthorised`, so a hidden section's route 403s).
        if (section.hidden) return false;
        if (section.key === ADMINISTRATION_SECTION_KEY) return showAdmin;
        if (frontend === 'desk') return isDeskSectionVisible(activePersona, section.key);
        return true;
    });
}

// --- Route authorisation (ADR-0013 / issue #209) ----------------------------
//
// The sidebar HIDES unauthorised entries, but a user who types or saves a URL
// can still reach the page. This seam closes that at the frontend: it decides,
// for a pathname, whether the ACTIVE persona may render the page IN PLACE — the
// same verdict the backend enforces with a 403. It is derived from the SAME
// policy the sidebar uses (`personaToFrontend`, `isDeskSectionVisible`,
// `grantsAdministration`, the section href tables) so visibility and access can
// never drift apart.

/** The four frontend route prefixes, in canonical order. */
const FRONTEND_ROUTE_PREFIXES: ReadonlyArray<readonly [string, FrontendKey]> = [
    ['/desk', 'desk'],
    ['/ops', 'ops'],
    ['/comply', 'comply'],
    ['/view', 'view'],
];

/**
 * Resolve the frontend a pathname belongs to from its leading segment, or
 * `null` for a non-frontend path (`/`, `/login`, `/not-authorised`). Matches on
 * a segment boundary so `/viewer` never resolves to the `view` shell.
 */
export function frontendForPathname(pathname: string): FrontendKey | null {
    for (const [prefix, key] of FRONTEND_ROUTE_PREFIXES) {
        if (pathname === prefix || pathname.startsWith(`${prefix}/`)) return key;
    }
    return null;
}

/**
 * True when `pathname` is at, or nested under, `href` — matched on a segment
 * boundary so `/desk/suitability/anything` matches `/desk/suitability` but
 * `/desk/suitability-extra` does not.
 */
function pathnameUnderHref(pathname: string, href: string): boolean {
    return pathname === href || pathname.startsWith(`${href}/`);
}

/**
 * The most specific route (canonical `href` or any `alias`) a section owns for
 * this pathname, or `null` if the section does not own it. Returns the matched
 * route string so callers can pick the longest match across sections.
 */
function sectionRouteMatch(section: SidebarSection, pathname: string): string | null {
    const routes = [section.href, ...(section.aliases ?? [])];
    let best: string | null = null;
    for (const route of routes) {
        if (!pathnameUnderHref(pathname, route)) continue;
        if (!best || route.length > best.length) best = route;
    }
    return best;
}

/**
 * The sidebar section a pathname falls under, by LONGEST-prefix match within the
 * frontend's section table (canonical href OR alias) — or `null` for a frontend
 * route that no section owns (the shell index `/desk`, or a detail route like
 * `/desk/fact-find/:id` whose section href is not itself a sidebar row).
 */
export function sectionForPathname(frontend: FrontendKey, pathname: string): SidebarSection | null {
    let best: SidebarSection | null = null;
    let bestLen = -1;
    for (const section of sectionsForFrontend(frontend)) {
        const match = sectionRouteMatch(section, pathname);
        if (match === null) continue;
        if (match.length > bestLen) {
            best = section;
            bestLen = match.length;
        }
    }
    return best;
}

/**
 * Whether the ACTIVE persona (with its granted tokens) may render the page at
 * `pathname`. The frontend guard mirror of the backend 403 — derived from the
 * same policy as the sidebar:
 *
 *   1. Non-frontend paths (`/`, `/login`, `/not-authorised`) are always allowed
 *      — they are not behind a frontend shell.
 *   2. The active persona must drive the pathname's frontend
 *      (`personaToFrontend`) — a desk-only user opening `/ops/*` is barred
 *      (cross-frontend).
 *   3. If a sidebar section owns the pathname, that section must be VISIBLE to
 *      the persona (`visibleSectionsFor`): an `im-assistant` is barred from
 *      `/desk/suitability` + `/desk/vulnerability` (and their sub-paths), and a
 *      non-admin is barred from `/view/administration` (and its sub-paths).
 *   4. A frontend route no section owns (shell index, a detail route whose
 *      parent section is authorised) is allowed once the frontend matches.
 */
export function isRouteAuthorised(
    pathname: string,
    activePersona: Persona,
    grantedTokens: readonly string[],
): boolean {
    const requiredFrontend = frontendForPathname(pathname);
    if (requiredFrontend === null) return true;

    if (personaToFrontend(activePersona) !== requiredFrontend) return false;

    const section = sectionForPathname(requiredFrontend, pathname);
    if (section === null) return true;

    return visibleSectionsFor(activePersona, grantedTokens).some((s) => s.key === section.key);
}
